import { ReactNode } from 'react';
import { Inbox, LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { CreateButton } from './CreateButton';
import { EMPTY_STATE_TITLE } from '../../styles/typography';

interface EmptyStateProps {
  title?: string;
  /**
   * The sentence saying why this is empty and what fills it. A `ReactNode` and
   * not a `string` so the copy can carry an inline affordance the sentence
   * itself names — the import history's "email one to your accounting address"
   * wraps that phrase in `ImportEmailHint`, which reveals and copies the real
   * address. Splitting the affordance out into a second line beneath would
   * leave the sentence pointing at something the reader still has to go find.
   */
  message?: ReactNode;
  /** Lucide icon for the illustration. Pass `null` to render no icon (dense inline empties). */
  icon?: LucideIcon | null;
  /** Action button - pass the entity name only (e.g., "Organization"); a Plus icon is added */
  action?: ReactNode | {
    label: string;
    onClick: () => void;
  };
  children?: ReactNode;
  className?: string;
  /**
   * **The size decision, in one question: is this empty state ALONE on the page?**
   *
   * - **No → `compact`.** Anything that shares its page with other content — a
   *   card/`CardSection` sitting above or below other sections, a tab body, a
   *   `<TableCell colSpan>` "no rows" row, a chart card with no data. Smaller
   *   icon, tighter padding, small sans title instead of the display serif; the
   *   title is omitted entirely when not supplied (message-only empties are
   *   common inline). A full-height illustration here pushes the rest of the
   *   page below the fold to say "nothing yet".
   * - **Yes → the default (full).** The empty state IS the page's content: a
   *   list page with no rows, a whole tab whose only child is the empty. This is
   *   the "create your first X" hero.
   */
  compact?: boolean;
}

export function EmptyState({
  title,
  message,
  icon: Icon = Inbox,
  action,
  children,
  className,
  compact = false,
}: EmptyStateProps) {
  // Full-variant dev fallbacks, kept out of JSX so the i18next lint rule doesn't
  // treat them as user-facing literals (they're only shown when a caller passes
  // neither title nor message).
  const fullTitle = title ?? 'No data';
  const fullMessage = message ?? 'There are no items to display.';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-4 py-6' : 'px-4 py-6 sm:py-8',
        className
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'text-muted-foreground/50',
            compact
              ? 'mb-2 h-8 w-8'
              : 'mb-3 h-10 w-10 sm:h-12 sm:w-12'
          )}
        />
      )}
      {/* Full variant renders title + message unconditionally (with historical
          English fallbacks + fixed spacing) so the ~40 existing callers are
          untouched. Compact renders only what's supplied — inline empties are
          typically message-only — with a smaller sans title. */}
      {compact ? (
        title && (
          <h3 className="mb-1 text-sm font-medium text-muted-foreground">{title}</h3>
        )
      ) : (
        <h3 className={cn('mb-1 text-muted-foreground', EMPTY_STATE_TITLE)}>
          {fullTitle}
        </h3>
      )}
      {compact ? (
        message && (
          <p className={cn('max-w-sm text-sm text-muted-foreground', action && 'mb-3')}>
            {message}
          </p>
        )
      ) : (
        <p className="mb-4 max-w-sm text-sm text-muted-foreground">{fullMessage}</p>
      )}
      {action && (
        typeof action === 'object' && 'onClick' in action ? (
          <CreateButton onClick={action.onClick} className="w-full max-w-xs sm:w-auto">
            {action.label}
          </CreateButton>
        ) : (
          <div className="w-full max-w-xs sm:w-auto">
            {action}
          </div>
        )
      )}
      {children}
    </div>
  );
}
